// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * SubAccountRowActions — per-row actions in the Clinic Team list (§5.1).
 *
 * Resend Invite (pending subs) → /api/users/resend-sub-invite.
 * Disable / Enable → /api/users/set-sub-account-status (disable blocks the
 * sub's login; patients stay assigned, so on disable we surface a reassign
 * prompt when assignedPatientCount > 0, per §7.1).
 * View Patients → the patient list.
 *
 * Quiet text buttons per the UI style guide (secondary/per-row actions stay
 * low-emphasis).
 */
import { Box, Button } from '@mui/material'
import React, { useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'
import { effectiveSubAccountStatus } from './subAccountStatus'

interface SubAccountRowActionsProps {
  sub: { id: number; subAccountStatus?: string | null }
  onChanged: () => void
}

const SubAccountRowActions: React.FC<SubAccountRowActionsProps> = ({ sub, onChanged }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [busy, setBusy] = useState(false)

  const status = effectiveSubAccountStatus(sub) // VP-TEAM-R04
  const isDisabled = status === 'disabled'

  const post = async (path: string, body: Record<string, unknown>) => {
    setBusy(true)
    try {
      const res = await deadlineFetch(path, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      return { ok: res.ok, status: res.status, data }
    } catch {
      notify('pos.clinic_team.error_network', { type: 'error' })
      return { ok: false, status: 0, data: {} as any }
    } finally {
      setBusy(false)
    }
  }

  const handleResend = async () => {
    const r = await post('/api/users/resend-sub-invite', { subAccountId: sub.id })
    if (r.ok) notify('pos.clinic_team.invite_resent', { type: 'success' })
    else if (r.status) notify('pos.clinic_team.error_generic', { type: 'error' })
  }

  const handleToggle = async () => {
    const next = isDisabled ? 'active' : 'disabled'
    const r = await post('/api/users/set-sub-account-status', {
      subAccountId: sub.id,
      status: next,
    })
    if (r.ok) {
      if (next === 'disabled') {
        const n = r.data?.assignedPatientCount ?? 0
        notify(n > 0 ? 'pos.clinic_team.disabled_reassign' : 'pos.clinic_team.disabled', {
          type: 'info',
          messageArgs: { count: n },
        })
      } else {
        notify('pos.clinic_team.enabled', { type: 'success' })
      }
      onChanged()
    } else if (r.status) {
      notify('pos.clinic_team.error_generic', { type: 'error' })
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {status === 'pending_invitation' && (
        <Button size="small" disabled={busy} onClick={handleResend}>
          {translate('pos.clinic_team.action_resend')}
        </Button>
      )}
      <Button
        size="small"
        disabled={busy}
        color={isDisabled ? 'primary' : 'inherit'}
        onClick={handleToggle}
      >
        {translate(isDisabled ? 'pos.clinic_team.action_enable' : 'pos.clinic_team.action_disable')}
      </Button>
      <Button
        size="small"
        disabled={busy}
        onClick={() => {
          // HashRouter: the query string lives INSIDE the hash, so the filter
          // has to be written here and not via window.location.search (which
          // stays empty under a hash route — the trap PetList documents).
          const filter = encodeURIComponent(JSON.stringify({ assignedVet: sub.id }))
          window.location.hash = `#/pets?filter=${filter}`
        }}
      >
        {translate('pos.clinic_team.action_view_patients')}
      </Button>
    </Box>
  )
}

export default SubAccountRowActions
